'use client';

import PageHeader from '@/app/settings/components/PageHeader';
import { Icons } from '@/components/icons';
import OverlayLoader from '@/components/ui/overlay-loader';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import { cn } from '@/lib/cn';
import { useSession } from '@/providers/session-provider';
import { useFetchAllIds } from '@/requests/hooks/use-fetch-all-ids';
import { useFetchAllInstances } from '@/requests/hooks/use-fetch-all-instances';
import type { OrganizationUser } from '@pigello/pigello-matrix';
import {
  OrganizationUserConfig,
  OrganizationUserWorkEngagementConfig,
  type OrganizationUserWorkEngagement,
} from '@pigello/pigello-matrix';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { ChristmasHat } from './components/christmas-hat';
import { DashboardNotes } from './components/dashboard-notes';
import { DashboardNotifications } from './components/dashboard-notifications';
import { HandleDashboardModal } from './components/handle-dashboard-modal';
import { HiddenOnDynamicEnvs } from '@/components/hidden-on-dynamic-envs';
import { IntegrationUpgradesBanner } from './components/integration-upgrades-banner';
import { KeyIndexes } from './components/key-indexes';
import { ReleaseNoteDashboardBanner } from './components/release-note-banner';
import { SnowEffect } from './components/snow-effect';
import { SystemEvents } from './components/system-events';
import { ToHandleWidget } from './components/to-handle-widget';
import { WorkflowsWidget } from './components/workflows-widget';
import { useGetDashboards } from './hooks/use-get-dashboard';
import { useGetDashboardWorkEngagement } from './hooks/use-get-dashboard-external-client';
import type { DashboardSettings } from './types';
import { getGreeting } from './utils';

export default function DashboardPage() {
  const {
    data: {
      session: { userId },
      user,
    },
  } = useSession();

  const { data: workEngagements, isLoading: isLoadingWorkEngagements } =
    useFetchAllInstances<OrganizationUserWorkEngagement>({
      modelName: OrganizationUserWorkEngagementConfig.modelName,
      filters: {
        user: {
          noop: userId,
        },
      },
      enabled: !!userId,
      nested: ['workEngagement'],
      enableGlobalFilters: true,
    });

  const workEngagementIds = [
    ...new Set(
      (workEngagements?.list ?? [])
        .map((w) => w.workEngagement?.id)
        .filter((id) => id)
    ),
  ] as string[];

  const [dashboardKey, setDashboardKey] = useLocalStorage<string | undefined>(
    'dashboardKey',
    undefined
  );

  const {
    data: dashboards,
    isPending: isLoadingDashboards,
    isRefetching: isLoadingDashboardsRefetching,
  } = useGetDashboards();

  // On first render, prefer the favorite dashboard; fallback to the first available
  const hasSetDefaultDashboard = useRef(false);
  useEffect(() => {
    if (dashboardKey || hasSetDefaultDashboard.current) {
      return;
    }
    const favorite = dashboards?.find((d) => d.ranking === 0);
    const targetKey = favorite?.key ?? dashboards?.[0]?.key;
    if (!targetKey) return;
    setDashboardKey(targetKey);
    hasSetDefaultDashboard.current = true;
  }, [dashboards, dashboardKey, setDashboardKey]);

  const workEngagementIdsCount = workEngagementIds.length;
  const {
    data: workEngagementsDashboards,
    isLoading: isLoadingWorkEngagementsDashboards,
    isFetched: isWorkEngagementsDashboardsFetched,
  } = useGetDashboardWorkEngagement({
    workEngagementIds: workEngagementIds,
    enabled: workEngagementIdsCount > 0,
  });

  const isLoading =
    isLoadingDashboards ||
    isLoadingWorkEngagementsDashboards ||
    isLoadingWorkEngagements;

  const allDashboards = useMemo(
    () => [
      ...(Array.isArray(dashboards) ? dashboards : []),
      ...(Array.isArray(workEngagementsDashboards)
        ? workEngagementsDashboards
        : []),
    ],
    [dashboards, workEngagementsDashboards]
  );

  const currentDashboard = allDashboards?.find(
    (dashboard) => dashboardKey === dashboard.key
  );

  const favoriteDashboard = dashboards?.find(
    (dashboard) => dashboard.ranking === 0
  );
  const isWorkEngagementDashboard =
    !!currentDashboard && 'work_engagement_id' in currentDashboard;

  const createdByOrganizationUserIds = (workEngagementsDashboards ?? [])?.map(
    (we) => we.created_by_user_id
  );

  const [dashboardSettings] = useLocalStorage<DashboardSettings>(
    'dashboardSettings',
    { hiddenItems: {} }
  );

  const hiddenKeyIndexes = dashboardSettings.hiddenItems['key-indexes'] ?? [];

  const { data: createdByOrganizationUsers } = useFetchAllIds<OrganizationUser>(
    {
      modelName: OrganizationUserConfig.modelName,
      ids: createdByOrganizationUserIds,
      idAttr: 'id',
      enabled: !!createdByOrganizationUserIds?.length,
    }
  );

  const firstDashboardKey = dashboards?.[0]?.key;
  const firstWorkEngagementDashboardKey = workEngagementsDashboards?.[0]?.key;

  const isDashboardKeyResolutionReady =
    !isLoadingDashboards &&
    !isLoadingDashboardsRefetching &&
    !isLoadingWorkEngagements &&
    (workEngagementIdsCount === 0 || isWorkEngagementsDashboardsFetched);

  useEffect(() => {
    if (!isDashboardKeyResolutionReady) return;

    const dashboardKeyExists =
      dashboards?.some((d) => d.key === dashboardKey) ||
      workEngagementsDashboards?.some((d) => d.key === dashboardKey);

    if (dashboardKey && dashboardKeyExists) return;

    if (favoriteDashboard) {
      setDashboardKey(favoriteDashboard?.key);
    } else if (firstDashboardKey) {
      setDashboardKey(firstDashboardKey);
    } else if (firstWorkEngagementDashboardKey) {
      setDashboardKey(firstWorkEngagementDashboardKey);
    }
  }, [
    workEngagementsDashboards,
    dashboards,
    dashboardKey,
    favoriteDashboard,
    firstDashboardKey,
    firstWorkEngagementDashboardKey,
    isDashboardKeyResolutionReady,
    setDashboardKey,
  ]);

  const showKeyIndexes =
    !currentDashboard ||
    (currentDashboard && !hiddenKeyIndexes.includes(currentDashboard.key));

  const isChristmasMode = useFeatureFlag(FEATURE_FLAGS.CHRISTMAS_MODE);

  return (
    <div
      className={cn(
        'bg-background p-6 pt-0',
        isChristmasMode &&
          'bg-linear-to-b from-[#1a2742] via-[#2d4263] to-white'
      )}
    >
      {isChristmasMode && <SnowEffect />}
      <PageHeader className='relative mb-6 flex flex-col items-start bg-background'>
        <div className='relative z-20 flex w-full items-center justify-between pt-6'>
          <div className='relative'>
            <h2 className={cn(isChristmasMode && 'text-white')}>
              {isChristmasMode && <ChristmasHat />}
              {getGreeting()} {user.communicationName}{' '}
              {isChristmasMode ? '🎄' : '👋'}
            </h2>
          </div>
          <HandleDashboardModal
            dashboardKey={dashboardKey}
            setDashboardKey={setDashboardKey}
            dashboards={dashboards}
            workEngagementsDashboards={workEngagementsDashboards}
            isLoadingDashboards={isLoading}
            isWorkEngagementDashboard={isWorkEngagementDashboard}
            organizationUserWorkEngagements={workEngagements?.list}
            createdByOrganizationUsers={createdByOrganizationUsers?.list}
          />
        </div>
        <div
          className={cn(
            'relative z-20 flex items-center gap-2 text-sm font-medium text-muted-foreground',
            isChristmasMode && 'text-white'
          )}
        >
          <Icons.dateRange className='size-6' />
          <span>{DateTime.now().toFormat('cccc d LLL HH:mm')}</span>
        </div>
      </PageHeader>
      <div className='relative flex size-full flex-col'>
        {isLoadingDashboards && (
          <OverlayLoader message='Hämtar dashboards...' />
        )}
        <ReleaseNoteDashboardBanner />
        <HiddenOnDynamicEnvs>
          <IntegrationUpgradesBanner />
        </HiddenOnDynamicEnvs>
        <div
          className={cn(
            'grid grid-cols-8 gap-8 lg:grid-cols-7',
            isWorkEngagementDashboard && 'grid-cols-6 lg:grid-cols-6'
          )}
        >
          <div className='col-span-full'>
            <WorkflowsWidget
              isWorkEngagementDashboard={isWorkEngagementDashboard}
              dashboardWorkEngagementId={
                currentDashboard && 'work_engagement_id' in currentDashboard
                  ? currentDashboard.work_engagement_id
                  : undefined
              }
            />
          </div>

          {showKeyIndexes && (
            <div className='col-span-full'>
              <KeyIndexes
                dashboards={allDashboards}
                dashboardKey={dashboardKey}
                isWorkEngagementDashboard={isWorkEngagementDashboard}
              />
            </div>
          )}

          <div
            className={cn(
              'col-span-full lg:col-span-5',
              isWorkEngagementDashboard && 'col-span-full lg:col-span-full'
            )}
          >
            <ToHandleWidget
              dashboards={allDashboards}
              dashboardKey={dashboardKey}
              isWorkEngagementDashboard={isWorkEngagementDashboard}
            />
          </div>
          {/* <div
                className={cn(
                  'col-span-4 lg:col-span-2',
                  isWorkEngagementDashboard && 'col-span-3 lg:col-span-3'
                )}
              >
                <DashboardBookmarks />
              </div> */}
          {!isWorkEngagementDashboard && (
            <div className='col-span-full lg:col-span-2'>
              <DashboardNotes
                dashboards={allDashboards}
                dashboardKey={dashboardKey}
                isLoadingDashboards={isLoadingDashboards}
              />
            </div>
          )}
          <div
            className={cn(
              'col-span-full lg:col-span-5',
              isWorkEngagementDashboard && 'col-span-full lg:col-span-4'
            )}
          >
            <SystemEvents
              dashboards={allDashboards}
              dashboardKey={dashboardKey}
              isWorkEngagementDashboard={isWorkEngagementDashboard}
            />
          </div>
          <div
            className={cn(
              'col-span-full lg:col-span-2',
              isWorkEngagementDashboard && 'col-span-full lg:col-span-2'
            )}
          >
            <DashboardNotifications
              dashboards={allDashboards}
              dashboardKey={dashboardKey}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
